<template>
  <div>
    <header class="bulk-edit-sticky-header surface-0 flex align-items-center justify-content-between gap-3 mb-3">
      <div>
        <h2 class="m-0 p-0 font-light text-600">Edit Attendance Records</h2>
        <p v-if="attendanceIds.length" class="m-0 pt-1 text-xs text-300">
          Managing {{ attendanceIds.length }} attendance record(s).
        </p>
      </div>
      <div class="flex align-items-center justify-content-end flex-wrap gap-2">
        <div class="bulk-total surface-100 border-round px-3 py-2">
          <span class="block text-xs text-400">Rota Hours</span>
          <strong>{{ summaryTotals.roster }}</strong>
        </div>
        <div class="bulk-total surface-100 border-round px-3 py-2">
          <span class="block text-xs text-400">Worked Hours</span>
          <strong>{{ summaryTotals.worked }}</strong>
        </div>
        <div class="bulk-total surface-100 border-round px-3 py-2">
          <span class="block text-xs text-400">Net Hours</span>
          <strong>{{ summaryTotals.net }}</strong>
        </div>
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          class="surface-300 hover:surface-500"
          :disabled="submitting"
          @click="handleBack"
        />
      </div>
    </header>

    <div v-if="routeError" class="card">
      <Message severity="error" :closable="false">{{ routeError }}</Message>
    </div>

    <div v-else class="card overflow-x-auto">

      <DataTable
        :value="editableRows"
        dataKey="attendance_id"
        size="small"
        :loading="loading"
        scrollable
        tableStyle="min-width: 84rem; min-height: 20rem;"
      >
        <Column field="employee_name" header="Employee" frozen class="text-600" style="min-width: 6rem;">
          <template #body="slotProps">
            <span class="block font-bold">{{ slotProps.data.employee_name }}</span>
            <span class="block text-xs text-300">{{ slotProps.data.company_name }}</span>
            <span class="block text-xs text-300">@{{ slotProps.data.employee_id }}</span>
          </template>
        </Column>

        <Column header="Roster" class="text-600" style="width: 19rem; max-width: 19rem;">
          <template #body="slotProps">
            <div class="flex flex-column gap-2">
              <TimestampEditor
                :modelValue="previewRow(slotProps.data).rosterStart"
                :disabled="submitting"
                ariaLabel="Roster start"
                class="bulk-timestamp-editor"
                @update:modelValue="value => updateManualValue(slotProps.data, 'rosterStart', value)"
              />
              <TimestampEditor
                :modelValue="previewRow(slotProps.data).rosterEnd"
                :disabled="submitting"
                ariaLabel="Roster end"
                class="bulk-timestamp-editor"
                @update:modelValue="value => updateManualValue(slotProps.data, 'rosterEnd', value)"
              />
            </div>
          </template>
        </Column>

        <Column header="Void" class="text-600" style="min-width: 4rem;">
          <template #body="slotProps">
            <div class="flex flex-column gap-3">
              <div
                v-for="pair in ['start', 'end']"
                :key="`roster-${pair}`"
                class="flex align-items-center gap-2 white-space-nowrap"
              >
                <label
                  v-for="component in timestampComponents"
                  :key="component.key"
                  class="flex align-items-center gap-1 text-xs"
                  v-tooltip.top="zeroComponentTooltip('ROSTER', pair, component.label)"
                >
                  <Checkbox
                    :modelValue="slotProps.data.zeroRoster[pair][component.key]"
                    binary
                    :disabled="submitting"
                    :aria-label="`Set roster ${pair} ${component.label} to zero for ${slotProps.data.employee_name}`"
                    @update:modelValue="checked => setZeroComponent(slotProps.data, 'ROSTER', pair, component.key, checked)"
                  />
                  <span>{{ component.label }}</span>
                </label>
              </div>
            </div>
          </template>
        </Column>

        <Column header="Attendance" class="text-600" style="width: 19rem; max-width: 19rem;">
          <template #body="slotProps">
            <div class="flex flex-column gap-2">
              <TimestampEditor
                :modelValue="previewRow(slotProps.data).checkin"
                :disabled="submitting"
                ariaLabel="Attendance check-in"
                class="bulk-timestamp-editor"
                @update:modelValue="value => updateManualValue(slotProps.data, 'checkin', value)"
              />
              <TimestampEditor
                v-if="previewRow(slotProps.data).checkout"
                :modelValue="previewRow(slotProps.data).checkout"
                :disabled="submitting"
                ariaLabel="Attendance checkout"
                class="bulk-timestamp-editor"
                @update:modelValue="value => updateManualValue(slotProps.data, 'checkout', value)"
              />
              <Button
                v-else
                label="Add Checkout"
                icon="pi pi-plus"
                class="bg-cyan-700 hover:bg-cyan-500"
                :disabled="submitting"
                @click="addCheckout(slotProps.data)"
              />
            </div>
          </template>
        </Column>

        <Column header="Void" class="text-600" style="min-width: 12rem;">
          <template #body="slotProps">
            <div class="flex flex-column gap-3">
              <div
                v-for="pair in ['start', 'end']"
                :key="`attendance-${pair}`"
                class="flex align-items-center gap-2 white-space-nowrap"
              >
                <label
                  v-for="component in timestampComponents"
                  :key="component.key"
                  class="flex align-items-center gap-1 text-xs"
                  v-tooltip.top="zeroComponentTooltip('ATTENDANCE', pair, component.label)"
                >
                  <Checkbox
                    :modelValue="slotProps.data.zeroAttendance[pair][component.key]"
                    binary
                    :disabled="submitting || (pair === 'end' && !slotProps.data.manual.checkout)"
                    :aria-label="`Set attendance ${pair} ${component.label} to zero for ${slotProps.data.employee_name}`"
                    @update:modelValue="checked => setZeroComponent(slotProps.data, 'ATTENDANCE', pair, component.key, checked)"
                  />
                  <span>{{ component.label }}</span>
                </label>
                <label
                  v-if="pair === 'end'"
                  class="flex align-items-center gap-1 text-xs"
                  v-tooltip.top="overtimeTooltip(slotProps.data)"
                >
                  <Checkbox
                    :modelValue="approvalRequired(slotProps.data) && slotProps.data.overtimeApproved"
                    binary
                    :disabled="submitting || !approvalRequired(slotProps.data)"
                    :aria-label="`Approve overtime for ${slotProps.data.employee_name}`"
                    @update:modelValue="checked => setOvertimeApproved(slotProps.data, checked)"
                  />
                  <span>Over</span>
                </label>
              </div>
            </div>
          </template>
        </Column>

        <Column header="Status" class="text-600" style="min-width: 7rem;">
          <template #body="slotProps">
            <div class="flex flex-column align-items-start justify-content-around gap-3 h-full">
              <Tag
                :value="previewRow(slotProps.data).checkinStatus"
                :severity="statusSeverity(previewRow(slotProps.data).checkinStatus)"
              />
              <Tag
                :value="previewRow(slotProps.data).checkoutStatus"
                :severity="statusSeverity(previewRow(slotProps.data).checkoutStatus)"
              />
            </div>
          </template>
        </Column>

        <Column header="Total" class="text-600" style="min-width: 12rem;">
          <template #body="slotProps">
            <div class="flex flex-column gap-3">
              <span class="font-bold">{{ formatDuration(
                previewRow(slotProps.data).rosterStart,
                previewRow(slotProps.data).rosterEnd
              ) }}</span>
              <div class="flex flex-column gap-1">
                <span class="font-bold">Worked: {{ formatDuration(
                  previewRow(slotProps.data).checkin,
                  previewRow(slotProps.data).checkout
                ) }}</span>
                <span class="font-bold text-300">Net: {{ netDuration(slotProps.data) }}</span>
              </div>
            </div>
          </template>
        </Column>

        <template #loading>
          <div class="text-400 py-20">Loading selected attendance...</div>
        </template>
      </DataTable>

      <Message v-if="validationError" severity="warn" :closable="false" class="mt-3">
        {{ validationError }}
      </Message>
      <Message v-if="displayError" severity="error" :closable="false" class="mt-3">
        {{ displayError }}
      </Message>

      <footer class="flex justify-content-end gap-2 mt-3">
        <Button
          label="Cancel"
          class="surface-300 hover:surface-500"
          :disabled="submitting"
          @click="handleBack"
        />
        <Button
          label="Update Attendance"
          icon="pi pi-save"
          :loading="submitting"
          :disabled="changedRows.length === 0 || Boolean(validationError) || loading"
          @click="updateAttendance"
        />
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Message from 'primevue/message';
import Tag from 'primevue/tag';
import TimestampEditor from '@/components/TimestampEditor.vue';
import { useAttendanceBulkReconcile } from '@/composables/api/useAttendanceBulkReconcile';
import { useUtils } from '@/composables/useUtils';
import {
  parseAttendanceRouteFilters,
  parseAttendanceSortQuery,
} from '@/composables/useAttendanceRouteFilters';

const MAX_ATTENDANCE_IDS = 100;
const timestampComponents = [
  { key: 'minutes', label: 'Mins' },
  { key: 'seconds', label: 'Sec' },
];
const route = useRoute();
const router = useRouter();
const toast = useToast();
const { parseUTCDate, getExactTimestamp, calculateNetWorkedHours } = useUtils();
const editableRows = ref([]);
const initialized = ref(false);
const loadError = ref(null);
const saveError = ref(null);
const {
  attendanceRecords,
  loading,
  submitting,
  error,
  fetchAttendanceRecords,
  reconcileAttendanceTimes,
} = useAttendanceBulkReconcile();

const parsedRoute = computed(() => {
  const rawAttendanceIds = route.params.attendanceIds;
  if (typeof rawAttendanceIds !== 'string' || rawAttendanceIds.trim() === '') {
    return { attendanceIds: [], error: 'No attendance IDs were provided.' };
  }

  const segments = rawAttendanceIds.split(',');
  if (segments.some(segment => !/^\d+$/.test(segment))) {
    return { attendanceIds: [], error: 'Attendance IDs must be positive integers separated by commas.' };
  }
  const attendanceIds = segments.map(Number);
  if (attendanceIds.some(id => !Number.isSafeInteger(id) || id <= 0)) {
    return { attendanceIds: [], error: 'One or more attendance IDs are invalid.' };
  }
  if (new Set(attendanceIds).size !== attendanceIds.length) {
    return { attendanceIds: [], error: 'Attendance IDs must not contain duplicates.' };
  }
  if (attendanceIds.length > MAX_ATTENDANCE_IDS) {
    return {
      attendanceIds: [],
      error: `A maximum of ${MAX_ATTENDANCE_IDS} attendance records can be edited at once.`,
    };
  }
  return { attendanceIds, error: null };
});

const attendanceIds = computed(() => parsedRoute.value.attendanceIds);
const routeError = computed(() => (
  parsedRoute.value.error || (initialized.value ? loadError.value : null)
));
const displayError = computed(() => saveError.value);

const safeReturnTo = computed(() => {
  const returnTo = route.query.returnTo;
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return null;
  }
  const resolvedRoute = router.resolve(returnTo);
  if (resolvedRoute.name !== 'ViewAttendanceFiltered') return null;
  const filters = parseAttendanceRouteFilters(resolvedRoute.params);
  const sort = parseAttendanceSortQuery(resolvedRoute.query);
  return filters.valid && sort.valid ? resolvedRoute.fullPath : null;
});

const cloneDate = value => value ? new Date(value) : null;
const createComponentSelection = () => ({
  start: { minutes: false, seconds: false },
  end: { minutes: false, seconds: false },
});

function zeroTimestampComponent(target, component) {
  if (!target) return;
  if (component === 'minutes') {
    target.setMinutes(0);
  } else if (component === 'seconds') {
    target.setSeconds(0, 0);
  }
}

function applyZeroSelection(target, selection) {
  const result = cloneDate(target);
  if (!result) return result;
  timestampComponents.forEach(({ key }) => {
    if (selection[key]) zeroTimestampComponent(result, key);
  });
  return result;
}

function previewRow(row) {
  const rosterStart = applyZeroSelection(row.manual.rosterStart, row.zeroRoster.start);
  const rosterEnd = applyZeroSelection(row.manual.rosterEnd, row.zeroRoster.end);
  const checkin = applyZeroSelection(row.manual.checkin, row.zeroAttendance.start);
  const checkout = applyZeroSelection(row.manual.checkout, row.zeroAttendance.end);

  return {
    rosterStart,
    rosterEnd,
    checkin,
    checkout,
    checkinStatus: getStatus(checkin, rosterStart, 'IN'),
    checkoutStatus: getStatus(checkout, rosterEnd, 'OUT'),
  };
}

function getStatus(actual, expected, type) {
  if (!actual || !expected) return 'NOT';
  const difference = (actual.getTime() - expected.getTime()) / 60000;
  if (type === 'IN') {
    if (difference >= -50 && difference <= 0) return 'ONTIME';
    return difference > 0 ? 'LATE' : 'EARLY';
  }
  if (difference >= 0 && difference < 50) return 'ONTIME';
  return difference >= 50 ? 'OVERTIME' : 'EARLY';
}

function formatDuration(start, end) {
  if (!start || !end || end < start) return '—';
  const totalMinutes = Math.floor((end - start) / 60000);
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`;
}

function netDuration(row) {
  const preview = previewRow(row);
  if (!preview.checkin || !preview.checkout) return '—';

  return calculateNetWorkedHours(
    preview.rosterStart,
    preview.rosterEnd,
    preview.checkin,
    preview.checkout,
    preview.checkinStatus,
    preview.checkoutStatus,
    approvalRequired(row) && row.overtimeApproved,
  );
}

function elapsedMinutes(start, end) {
  if (!start || !end || end < start) return 0;
  return Math.floor((end.getTime() - start.getTime()) / 60000);
}

function durationTextToMinutes(value) {
  const match = typeof value === 'string' ? value.match(/^(\d+):(\d{2})$/) : null;
  return match ? (Number(match[1]) * 60) + Number(match[2]) : null;
}

function formatTotalMinutes(totalMinutes) {
  const safeMinutes = Math.max(0, Number(totalMinutes) || 0);
  return `${String(Math.floor(safeMinutes / 60)).padStart(2, '0')}:${String(safeMinutes % 60).padStart(2, '0')}`;
}

const summaryTotals = computed(() => {
  let rosterMinutes = 0;
  let workedMinutes = 0;
  let netMinutes = 0;

  editableRows.value.forEach(row => {
    const preview = previewRow(row);
    rosterMinutes += elapsedMinutes(preview.rosterStart, preview.rosterEnd);
    workedMinutes += elapsedMinutes(preview.checkin, preview.checkout);

    const rowNet = netDuration(row);
    const rowNetMinutes = durationTextToMinutes(rowNet);
    if (rowNetMinutes != null) netMinutes += rowNetMinutes;
  });

  return {
    roster: formatTotalMinutes(rosterMinutes),
    worked: formatTotalMinutes(workedMinutes),
    net: formatTotalMinutes(netMinutes),
  };
});

function approvalRequired(row) {
  const preview = previewRow(row);
  return Boolean(preview.checkout)
    && (preview.checkinStatus === 'EARLY' || preview.checkoutStatus === 'OVERTIME');
}

function statusSeverity(status) {
  return {
    ONTIME: 'secondary',
    LATE: 'warn',
    EARLY: 'info',
    OVERTIME: 'success',
    NOT: 'secondary',
  }[status] || 'secondary';
}

function updateManualValue(row, field, value) {
  const nextValue = cloneDate(value);
  const currentValue = row.manual[field];
  const zeroSelection = {
    rosterStart: row.zeroRoster.start,
    rosterEnd: row.zeroRoster.end,
    checkin: row.zeroAttendance.start,
    checkout: row.zeroAttendance.end,
  }[field];

  if (nextValue && currentValue && zeroSelection) {
    timestampComponents.forEach(({ key }) => {
      if (zeroSelection[key]) {
        if (key === 'minutes') nextValue.setMinutes(currentValue.getMinutes());
        if (key === 'seconds') nextValue.setSeconds(currentValue.getSeconds(), currentValue.getMilliseconds());
      }
    });
  }

  row.manual[field] = nextValue;
  saveError.value = null;
}

function addCheckout(row) {
  row.manual.checkout = cloneDate(row.manual.rosterEnd);
  saveError.value = null;
}

function setZeroComponent(row, target, pair, component, checked) {
  const selection = target === 'ROSTER' ? row.zeroRoster : row.zeroAttendance;
  selection[pair][component] = checked;
  saveError.value = null;
}

function setOvertimeApproved(row, checked) {
  row.overtimeApproved = checked;
  saveError.value = null;
}

function overtimeTooltip(row) {
  return approvalRequired(row)
    ? 'Approve payable early or overtime attendance'
    : 'Overtime approval is not required for these times';
}

function zeroComponentTooltip(target, pair, component) {
  const targetLabel = target === 'ROSTER' ? 'roster' : 'attendance';
  const eventLabel = pair === 'start' ? 'start/check-in' : 'end/checkout';
  return `Set ${targetLabel} ${eventLabel} ${component.toLowerCase()} to 00`;
}

function rowPayload(row) {
  const preview = previewRow(row);
  return {
    attendance_id: Number(row.attendance_id),
    roster_start: getExactTimestamp(preview.rosterStart),
    roster_end: getExactTimestamp(preview.rosterEnd),
    attendance_checkin: getExactTimestamp(preview.checkin),
    attendance_checkout: preview.checkout ? getExactTimestamp(preview.checkout) : null,
    over_below_status: approvalRequired(row) && row.overtimeApproved,
  };
}

function originalPayload(row) {
  return {
    attendance_id: Number(row.attendance_id),
    roster_start: Number(row.roster_start),
    roster_end: Number(row.roster_end),
    attendance_checkin: Number(row.attendance_checkin),
    attendance_checkout: row.attendance_checkout == null ? null : Number(row.attendance_checkout),
    over_below_status: Boolean(row.over_below_status),
  };
}

const changedRows = computed(() => editableRows.value.filter(row => (
  JSON.stringify(rowPayload(row)) !== JSON.stringify(originalPayload(row))
)));

const validationError = computed(() => {
  for (const row of editableRows.value) {
    const preview = previewRow(row);
    if (preview.rosterEnd < preview.rosterStart) {
      return `Roster end cannot be earlier than roster start for ${row.employee_name}.`;
    }
    if (preview.checkout && preview.checkout < preview.checkin) {
      return `Checkout cannot be earlier than check-in for ${row.employee_name}.`;
    }
  }
  return null;
});

function parseTimestamp(timestamp) {
  return parseUTCDate(new Date(Number(timestamp)).toISOString());
}

async function loadRows() {
  loadError.value = null;
  saveError.value = null;
  const loaded = await fetchAttendanceRecords(attendanceIds.value);
  if (!loaded) {
    loadError.value = error.value;
    return false;
  }

  editableRows.value = attendanceRecords.value.map(record => ({
    ...record,
    manual: {
      rosterStart: parseTimestamp(record.roster_start),
      rosterEnd: parseTimestamp(record.roster_end),
      checkin: parseTimestamp(record.attendance_checkin),
      checkout: record.attendance_checkout == null
        ? null
        : parseTimestamp(record.attendance_checkout),
    },
    zeroRoster: createComponentSelection(),
    zeroAttendance: createComponentSelection(),
    overtimeApproved: Boolean(record.over_below_status),
  }));
  return true;
}

function navigateBackOrFallback(replaceFallback = false) {
  if (safeReturnTo.value) {
    router.replace(safeReturnTo.value);
  } else if (window.history.state?.back) {
    router.back();
  } else if (replaceFallback) {
    router.replace({ name: 'ViewAttendance' });
  } else {
    router.push({ name: 'ViewAttendance' });
  }
}

const handleBack = () => navigateBackOrFallback(false);

async function updateAttendance() {
  if (!changedRows.value.length || validationError.value) return;
  saveError.value = null;
  const result = await reconcileAttendanceTimes(changedRows.value.map(rowPayload));
  if (!result.success) {
    saveError.value = result.error;
    return;
  }

  const reloaded = await loadRows();
  toast.add({
    severity: 'success',
    summary: 'Attendance updated',
    detail: result.data.message,
    life: 4000,
    group: 'br',
  });
  if (reloaded) navigateBackOrFallback(true);
}

onMounted(async () => {
  if (!parsedRoute.value.error) await loadRows();
  initialized.value = true;
});
</script>

<style scoped>
.bulk-edit-sticky-header {
  position: sticky;
  top: 0;
  z-index: 30;
  padding-block: 0.65rem;
  box-shadow: 0 1px 0 var(--p-content-border-color);
}

.bulk-total {
  min-width: 7.5rem;
  line-height: 1.15;
}

:deep(.bulk-timestamp-editor) {
  width: 17rem;
  max-width: 100%;
}

@media (max-width: 70rem) {
  .bulk-edit-sticky-header {
    align-items: flex-start !important;
    flex-direction: column;
  }
}
</style>
